import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { Avatar } from "@moe/oss/ui/avatar";
import { Banner } from "@moe/oss/ui/banner";
import { Button } from "@moe/oss/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@moe/oss/ui/form";
import { Input } from "@moe/oss/ui/input";
import { Textarea } from "@moe/oss/ui/textarea";
import { supportedImageExtsWithDot } from "@moe/oss/utils/utils";
import { config } from "@moe/priv/config";
import { profileConfig, ProfileSelf } from "@moe/priv/model/profile";
import { DialogConfig } from "@moe/priv/types/types";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useAppContext } from "@web/route-services/root/AppContext";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { ImageCropModal } from "./ImageCropModal";
import { PostgrestError } from "@supabase/supabase-js";

const schema = z.object({
  username: z
    .string()
    .min(profileConfig.usernameMinChars)
    .max(profileConfig.usernameMaxChars)
    .regex(profileConfig.usernameRegex),
  bio: z.string().min(profileConfig.bioMinChars).max(profileConfig.bioMaxChars).optional(),
  avatar: z
    .instanceof(File)
    .refine(
      (file) => file.size <= profileConfig.avatarMaxFileSizeBytes,
      `Avatar file size should be less than ${profileConfig.avatarMaxFileSizeBytes / 1e6}MB`
    )
    .optional(),
  banner: z
    .instanceof(File)
    .refine(
      (file) => file.size <= profileConfig.bannerMaxFileSizeBytes,
      `Banner file size should be less than ${profileConfig.bannerMaxFileSizeBytes / 1e6}MB`
    )
    .optional()
});
type Schema = z.infer<typeof schema>;
interface Props {
  profile: ProfileSelf;
}

export function ProfileForm({ profile }: Props) {
  const [cropImage, setCropImage] = useState<{ file: File; type: "avatar" | "banner" } | null>(null);
  const [bannerDisplayImage, setBannerDisplayImage] = useState<string | null>();
  const [avatarDisplayImage, setAvatarDisplayImage] = useState<string | null>();
  const [bannerInputRef, setBannerInputRef] = useState<HTMLInputElement | null>(null);
  const [avatarInputRef, setAvatarInputRef] = useState<HTMLInputElement | null>(null);
  const { createDialog, createModal, closeModal, access } = useAppContext();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: async (profile: Schema) => {
      await access.profile.updateSelf(profile);
      return profile;
    },
    onSuccess: async (profile) => {
      toast.success("Profile updated!");
      navigate({
        to: "/profile",
        search: {
          username: profile.username
        }
      });
    },
    onError: (error: PostgrestError) => {
      //assumes username is the only field that can give out 23505 code
      if (error.code === '23505') {
        toast.error("Username already taken, Sorry!")
      } else{
        toast.error("Error updating profile");
      }

      console.error(error);
    }
  });

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      username: profile?.username || "",
      bio: profile?.bio || "",
      avatar: undefined,
      banner: undefined
    }
  });

  useEffect(() => {
    form.reset({
      username: profile?.username || "",
      bio: profile?.bio || "",
      avatar: undefined,
      banner: undefined
    });
    setAvatarDisplayImage(profile?.avatar?.toString?.() ?? null);
    setBannerDisplayImage(profile?.banner?.toString?.() ?? null);
  }, [form, profile]);

  const onSubmit = (data: Schema) => {
    mutation.mutate(data);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>, fieldName: "avatar" | "banner") => {
    const file = event.target.files?.[0];
    if (!file) return;
    const minWidth = config[fieldName].stored.minWidth;
    const minHeight = config[fieldName].stored.minHeight;
    const aspect = config[fieldName].stored.aspect;

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      console.log({ width, height });
      if (width < minWidth || height < minHeight) {
        toast.error(`Image must be at least ${minWidth}x${minHeight} pixels`);
        return;
      }
      setCropImage({ file, type: fieldName });
      createModal({
        content: (
          <ImageCropModal
            minWidth={minWidth}
            minHeight={minHeight}
            aspect={aspect}
            src={URL.createObjectURL(file)}
            onCropComplete={(blob) => handleCropComplete(blob, fieldName)}
            onClose={() => closeModal()}
          />
        ),
        onInteractOutside: (e) => {
          e.preventDefault();
        }
      });
    };
    img.onerror = () => {
      toast(`Failed to load image.`);
    };
    event.target.value = "";
  };

  const handleCropComplete = (blob: Blob, fieldName: "avatar" | "banner") => {
    const file = new File([blob], cropImage?.file.name || `${fieldName}.jpg`, { type: "image/jpeg" });
    form.setValue(fieldName, file);
    const imageUrl = URL.createObjectURL(blob);
    if (fieldName === "avatar") {
      setAvatarDisplayImage(imageUrl);
    } else {
      setBannerDisplayImage(imageUrl);
    }
    closeModal();
  };

  const resetHandler = () => {
    const reset = () => {
      form.reset({
        username: profile?.username || "",
        bio: profile?.bio || "",
        avatar: undefined,
        banner: undefined
      });
      setAvatarDisplayImage(profile?.avatar?.toString?.() ?? null);
      setBannerDisplayImage(profile?.banner?.toString?.() ?? null);
    };

    const config: DialogConfig = {
      title: "Reset Form?",
      actionLabel: "Reset",
      description:
        "Are you sure you want to reset the form? All unsaved changes will be lost. This action cannot be undone.",
      onAction: reset
    };
    createDialog(config);
  };

  return (
    <div className="scroll-secondary bg-container-primary flex h-full w-full flex-col overflow-auto rounded-xl">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Banner picture */}
          <div className="relative mb-12 shrink-0">
            <FormField
              control={form.control}
              name="banner"
              render={({ field: { ref, onChange, value, ...field } }) => (
                <FormItem>
                  <FormControl>
                    <div
                      className="bg-action-tertiary flex h-48 w-full cursor-pointer items-center justify-center overflow-hidden opacity-75"
                      onClick={() => bannerInputRef?.click()}
                    >
                      {bannerDisplayImage ? (
                        <Banner src={bannerDisplayImage} className="h-48" />
                      ) : (
                        <PencilSquareIcon className="text-tx-primary absolute h-12 w-12" />
                      )}
                      <div className="absolute inset-0 bg-black opacity-0 transition-opacity duration-200 hover:opacity-10"></div>
                      <input
                        ref={(node) => {
                          ref(node);
                          setBannerInputRef(node);
                        }}
                        type="file"
                        className="hidden"
                        onChange={(e) => handleImageChange(e, "banner")}
                        accept={supportedImageExtsWithDot.join(",")}
                        {...field}
                      />
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Avatar picture */}
            <FormField
              control={form.control}
              name="avatar"
              render={({ field: { ref, onChange, value, ...field } }) => (
                <FormItem>
                  <FormControl>
                    <div
                      className="bg-action-tertiary absolute -bottom-12 left-4 flex h-24 w-24 cursor-pointer items-center justify-center overflow-hidden rounded-full"
                      onClick={() => avatarInputRef?.click()}
                    >
                      {avatarDisplayImage ? (
                        <Avatar src={avatarDisplayImage} className="size-24" />
                      ) : (
                        <PencilSquareIcon className="text-tx-primary absolute h-8 w-8" />
                      )}
                      <div className="absolute inset-0 bg-black opacity-0 transition-opacity duration-200 hover:opacity-10"></div>
                      <input
                        ref={(node) => {
                          ref(node);
                          setAvatarInputRef(node);
                        }}
                        type="file"
                        className="hidden"
                        onChange={(e) => handleImageChange(e, "avatar")}
                        accept={supportedImageExtsWithDot.join(",")}
                        {...field}
                      />
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {/* User details container */}
          <div className="px-6 pb-6 pt-12">
            <div className="flex flex-col space-y-5 pt-8">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-tx-primary">Username</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter a username" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="bio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-tx-primary">Bio</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Add a bio!" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="mt-8 flex justify-end space-x-3">
                <Button variant="clear" type="button" onClick={resetHandler}>
                  Reset
                </Button>
                <Button type="submit">
                  <PencilSquareIcon className="" />
                  Save
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
